<script setup lang="ts">
import {computed, ref} from 'vue'
import type {DashboardActivityDay} from '@shared/types'

const props = defineProps<{activity: DashboardActivityDay[]}>()

const CELL = 11
const GAP = 3
const LABEL_W = 30
const MONTH_H = 16

type Cell = {date: string; reviews: number; learned: number}

const weeks = computed<Array<Cell | null>[]>(() => {
  if (!props.activity.length) return []
  const first = new Date(`${props.activity[0].date}T00:00:00`)
  const offset = (first.getDay() + 6) % 7
  const cells: Array<Cell | null> = [...Array<null>(offset).fill(null), ...props.activity.map((a) => ({...a}))]
  const result: Array<Cell | null>[] = []
  for (let i = 0; i < cells.length; i += 7) {
    const week = cells.slice(i, i + 7)
    while (week.length < 7) week.push(null)
    result.push(week)
  }
  return result
})

function level(reviews: number): number {
  if (reviews <= 0) return 0
  if (reviews < 8) return 1
  if (reviews < 20) return 2
  if (reviews < 45) return 3
  return 4
}

const levelClass = ['fill-muted', 'fill-emerald-700/70', 'fill-emerald-600', 'fill-emerald-500', 'fill-emerald-400']

const monthLabels = computed(() => {
  const labels: Array<{x: number; label: string}> = []
  const formatter = new Intl.DateTimeFormat(undefined, {month: 'short'})
  let lastMonth = -1
  weeks.value.forEach((week, wi) => {
    const mid = week[3] ?? week.find(Boolean)
    if (!mid) return
    const date = new Date(`${mid.date}T00:00:00`)
    if (date.getMonth() !== lastMonth) {
      lastMonth = date.getMonth()
      labels.push({x: LABEL_W + wi * (CELL + GAP), label: formatter.format(date)})
    }
  })
  return labels
})

const weekdayLabels = computed(() => {
  const formatter = new Intl.DateTimeFormat(undefined, {weekday: 'short'})
  const monday = new Date('2024-01-01T00:00:00')
  return [0, 2, 4].map((row) => {
    const y = MONTH_H + row * (CELL + GAP) + CELL - 2
    return {y, label: formatter.format(new Date(monday.getTime() + row * 86400000))}
  })
})

const width = computed(() => LABEL_W + weeks.value.length * (CELL + GAP))

const hover = ref<{cell: Cell; clientX: number; clientY: number; flip: boolean} | null>(null)

function onEnter(cell: Cell, event: MouseEvent) {
  const target = event.target as SVGRectElement
  const box = target.getBoundingClientRect()
  hover.value = {
    cell,
    clientX: box.left + box.width / 2,
    clientY: box.top,
    flip: box.left > window.innerWidth - 220,
  }
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'})
</script>

<template>
  <div class="w-full">
    <div class="scrollbar-laconic overflow-x-auto pb-1">
      <svg :width="width" :height="MONTH_H + 7 * (CELL + GAP)" class="block" role="img">
        <text v-for="(l, i) in weekdayLabels" :key="`wd-${i}`" :x="LABEL_W - 6" :y="l.y + 4" text-anchor="end" class="fill-muted-foreground text-[9px]">
          {{ l.label }}
        </text>
        <text v-for="(m, i) in monthLabels" :key="`m-${i}`" :x="m.x" y="10" class="fill-muted-foreground text-[9px]">{{ m.label }}</text>
        <g v-for="(week, wi) in weeks" :key="`w-${wi}`">
          <template v-for="(cell, di) in week" :key="`${wi}-${di}`">
            <rect
              v-if="cell"
              :x="LABEL_W + wi * (CELL + GAP)"
              :y="MONTH_H + di * (CELL + GAP)"
              :width="CELL"
              :height="CELL"
              rx="2.5"
              :class="levelClass[level(cell.reviews)]"
              @mouseenter="onEnter(cell, $event)"
              @mouseleave="hover = null"
            />
          </template>
        </g>
      </svg>
    </div>

    <div class="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
      <span>{{ $t('home.dashboard.heatmapHint') }}</span>
      <span class="flex shrink-0 items-center gap-1.5">
        {{ $t('home.dashboard.less') }}
        <svg :width="5 * (CELL + GAP)" :height="CELL">
          <rect v-for="i in 5" :key="i" :x="(i - 1) * (CELL + GAP)" y="0" :width="CELL" :height="CELL" rx="2.5" :class="levelClass[i - 1]" />
        </svg>
        {{ $t('home.dashboard.more') }}
      </span>
    </div>

    <Teleport to="body">
      <div
        v-if="hover"
        class="pointer-events-none fixed z-50 rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md"
        :style="{
          left: `${hover.clientX}px`,
          top: `${hover.clientY}px`,
          transform: hover.flip ? 'translate(calc(-100% - 8px), -110%)' : 'translate(-50%, -110%)',
        }">
        {{ dateFormatter.format(new Date(`${hover.cell.date}T00:00:00`)) }} · {{ $t('home.dashboard.reviewsCount', {n: hover.cell.reviews}) }}
      </div>
    </Teleport>
  </div>
</template>
